/**
 * Browser half of the OAuth popup-completion contract (PR #4858): the callback popup
 * postMessages its result here; the settings page refreshes in place. Extracted from
 * GoogleIntegrations.tsx so the origin gate and branching are directly testable.
 *
 * Origin gate: the popup page is served from this origin (preprod routes /api under the admin
 * domain) or from the API host (prod: api.pagemind.ai) — accept only pagemind.ai hosts, the
 * page's own host, and localhost (dev). A forged message can at worst trigger a status
 * re-fetch from our own backend plus a toast; no state is taken from the event itself.
 */
export interface OAuthMessageDeps {
  toast: (args: { title: string; description?: string; variant?: 'destructive' }) => unknown;
  /** Refresh the page's status. Resolves true on success, false if the fetch failed. */
  loadStatus: () => Promise<boolean>;
  /**
   * The site the page is currently showing. The popup completes for the site the flow was
   * STARTED for (`data.siteId`, set by the backend callback); if the user switched sites while
   * the popup was open, the two differ and this page must not refresh or open Configure as if
   * the connection were its own (codex review, PR #5479).
   */
  currentSiteId: string | undefined;
  /**
   * Put the user in front of the property pickers. Before this existed the success toast said
   * "now choose which properties map to this site" while the page stayed on the Status tab —
   * the instruction pointed at a tab the user could not see, and connections were left with
   * no property selected. Called only on the success branch.
   */
  openConfigure: () => void;
  currentHostname?: string;
}

export type OAuthMessageOutcome =
  | 'success'
  | 'error'
  | 'ignored'
  | 'site_mismatch'   // completed for a site other than the one on screen
  | 'refresh_failed'; // connected, but the status refresh failed — Configure NOT opened

export async function handleGoogleOAuthMessage(
  event: { data: any; origin: string },
  { toast, loadStatus, openConfigure, currentSiteId, currentHostname }: OAuthMessageDeps
): Promise<OAuthMessageOutcome> {
  const data = event.data;
  if (!data || data.source !== 'pagemind-google-oauth') return 'ignored';

  let host: string;
  try {
    host = new URL(event.origin).hostname;
  } catch {
    return 'ignored';
  }
  // Exact allowlist (review minor on #4858): only the hosts that can legitimately serve the
  // callback page — this page's own host, the API hosts, and dev loopback. No wildcard: a
  // compromised unrelated subdomain must not be able to spoof completion messages.
  const ownHost = currentHostname ?? window.location.hostname;
  const trusted = new Set([ownHost, 'api.pagemind.ai', 'api-preprod.pagemind.ai', 'localhost', '127.0.0.1']).has(host);
  if (!trusted) return 'ignored';

  if (data.success === 'true') {
    if (!currentSiteId || data.siteId !== currentSiteId) {
      toast({
        title: 'Google account connected to a different site',
        description: `The connection completed for the site the flow was started from${
          data.email ? ` (as ${data.email})` : ''
        }. Switch to that site to choose its properties.`,
      });
      return 'site_mismatch';
    }
    // Configure is only useful once the refreshed status says "connected". Opening it before —
    // or after a failed refresh — shows "Connect a Google account first" right under a
    // "connected" toast.
    const refreshed = await loadStatus();
    if (!refreshed) {
      toast({
        title: 'Google account connected, but the page could not refresh',
        description: 'Reload the page to see the connection and choose which properties map to this site.',
        variant: 'destructive',
      });
      return 'refresh_failed';
    }
    toast({
      title: 'Google account connected',
      description: data.email
        ? `Connected as ${data.email}. Now choose which properties map to this site.`
        : 'Connection completed. Now choose which properties map to this site.',
    });
    openConfigure();
    return 'success';
  }
  if (data.error) {
    toast({
      title: 'Google connection failed',
      description: data.description || data.error,
      variant: 'destructive',
    });
    return 'error';
  }
  return 'ignored';
}
